<script setup lang="ts">
import type { Location } from "@/models"
import { computed, onMounted, ref } from "vue"
import { Input } from "@/components/ui"
import { useGooglePlaceSearch } from "@/composables"

interface GooglePlaceInputProps {
  id: string
  name: string
  label: string
  hint?: string
  disabled?: boolean
  error?: string
}

withDefaults(defineProps<GooglePlaceInputProps>(), {
  hint: "",
  disabled: false,
  error: "",
})

const emit = defineEmits<{
  (e: "update:location", location?: Location): void
  (e: "error", error: unknown): void
}>()

const locationInput = ref<InstanceType<typeof Input> | null>(null)
const location = ref<Location | undefined>()

const { autocomplete, load, extractPlaceDetails } = useGooglePlaceSearch(import.meta.env.VITE_GOOGLE_MAPS_API_KEY)

async function loadGoogleMaps() {
  if (!locationInput.value?.inputRef)
    return

  try {
    await load(locationInput.value.inputRef)

    autocomplete.value?.addListener("place_changed", async () => {
      try {
        const place = autocomplete.value?.getPlace()

        if (!place)
          return

        location.value = extractPlaceDetails(place)
        emit("update:location", location.value)
      }
      catch (error) {
        emit("error", error)
      }
    })
  }
  catch (error) {
    emit("error", error)
  }
}

function clearLocation() {
  location.value = undefined
  emit("update:location", location.value)
}

onMounted(loadGoogleMaps)

const displayValue = computed<string>(() => {
  if (location.value?.displayAddress) {
    return location.value?.displayAddress
  }

  return (location.value?.establishment || location.value?.pointOfInterest) && location.value.name
    ? `${location.value?.name}, ${location.value?.formattedAddress}`
    : location.value?.formattedAddress ?? ""
})
</script>

<template>
  <Input
    :id="id"
    ref="locationInput"
    :name="name"
    :label="label"
    :error="error"
    :hint="hint"
    :disabled="disabled"
    label-icon="Location"
    clearable
    autocomplete="off"
    :model-value="displayValue"
    @clear="clearLocation"
    @input="clearLocation"
    @keydown.enter.prevent
  />
</template>
